// Storefront Styles panel — the Squarespace "Site Styles" pattern, in Fluent + the AC theme.
//
// Three drill-in levels inside one Panel, driven by a tiny view state machine:
//
//   ROOT      one row per category (Buttons · Product Cards · Sections · Typography · Forms) with a
//             representative swatch, a count, a global-styles hint and a chevron.
//   CATEGORY  "‹ Storefront Styles" back header, the category name, one card per style
//             (swatch + name + scope badge + overflow menu), "New style", and the seed empty state.
//   STYLE     "‹ Buttons" back header, a large preview that repaints from the draft as you type,
//             then a flat-row editing surface grouped per part (BASE / HOVER / LABEL …) with an
//             "Advanced…" escape into the existing key/value CSS editor for that part's dict.
//
// The in-progress edit is reported upward via `onDraftChange` so the pages editor can merge it into
// the list it publishes through <StorefrontStylesProvider>. That is what makes an unsaved tweak
// here repaint every element in the page preview that references the style — including the section
// previews and the product cards inside them.
//
// Everything except that in-progress edit is committed immediately through the RTK mutations
// (create/duplicate/rename/delete/promote/seed); preview-vs-live scoping is the API's job, same as
// sections.

import React from 'react';
import {
    ActionButton,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Icon,
    IContextualMenuItem,
    IconButton,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    TextField,
    useTheme,
} from '@fluentui/react';
import { Css } from 'nano-css/addon/vcssom/cssToTree';
import { toast } from 'react-toastify';
import { IStorefrontSettings, IStorefrontStyle, IStorefrontStyleParts, IStorefrontStyleSaveRequest, StorefrontStyleElementType } from '../../../models/Storefront';
import { ISearchElementStyle } from './searchPageConfig';
import {
    cardStyleFrom,
    elementStyleFrom,
    ElementPropertyRows,
    priceStyleFrom,
    StyleGroupAccordion,
    textStyleFrom,
} from './styleControls';
import ProductCardExample from './ProductCardExample';
import StorefrontSectionAdvancedStyleEditorModal from '../StorefrontSectionAdvancedStyleEditorModal';
import { IStyleBulkApplyPlanEntry } from './styleBulkApply';
import { IDefaultStyleMap } from './styleConfig';
import { ColorRow, StyleRow, useCellControlStyles } from './styleRows';
import FontPicker from '../../Inputs/FontPicker';
import PaletteTokenizeDialog from './PaletteTokenizeDialog';
import { applyColorTokens, IColorTokenMapping } from './paletteTokenize';
import {
    countStyleRefs,
    emptyPartsFor,
    FONT_SLOT_LABELS,
    FONT_SLOTS,
    FontSlot,
    isGlobalStyle,
    isTokenElementType,
    nextSortOrder,
    PALETTE_SLOT_LABELS,
    PALETTE_SLOTS,
    PALETTE_STRIP_SLOTS,
    PaletteSlot,
    sortedStyles,
    STYLE_ELEMENT_TYPE_LABELS,
    STYLE_ELEMENT_TYPE_SINGULAR,
    STYLE_ELEMENT_TYPES,
    STYLE_PART_ICONS,
    STYLE_PART_LABELS,
    STYLE_PARTS,
} from './stylesConfig';
import {
    useActivateStorefrontStyleMutation,
    useDeleteStorefrontStyleMutation,
    usePromoteStorefrontStyleMutation,
    useSaveStorefrontStyleMutation,
    useSeedStorefrontStylesMutation,
} from '../../../store/endpoints/StorefrontStyleEndpoints';

interface IStorefrontStylesPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    /** The storefront being edited — the scope new styles are created in. */
    storefront: string;
    /** The server list (own storefront styles ∪ org globals). */
    styles: IStorefrontStyle[];
    isLoading?: boolean;
    /** The style currently being edited but not yet saved, so the caller can preview it. */
    onDraftChange: (draft: IStorefrontStyle | undefined) => void;
    /**
     * Objects to scan for `$styleRef` when reporting a style's usage count on delete — the working
     * settings object (whose JSON config blobs carry refs) and the working sections list. Absent
     * ⇒ the delete dialog falls back to a generic warning.
     */
    usageScanRoots?: unknown[];
    /**
     * "Apply to all …" support. Both must be supplied for the menu item to appear:
     * `styleBulkApplyPlan` spells out what would be touched (for the confirm dialog),
     * `onApplyStyleToAll` performs it against the pages editor's working config drafts and
     * returns how many element keys actually changed. Omit both to hide the affordance.
     */
    styleBulkApplyPlan?: (elementType: StorefrontStyleElementType) => IStyleBulkApplyPlanEntry[];
    onApplyStyleToAll?: (style: IStorefrontStyle) => number;
    /**
     * Default-style support (both must be supplied for the menu items / badge to appear).
     * `defaultStyleIds` is the storefront's current choice per element type (styleConfig
     * defaultStyles); `onSetDefaultStyle` writes it to the working settings draft — it persists
     * through the pages editor's normal save, not an API call of its own. New sections seed their
     * $styleRefs from these defaults.
     */
    defaultStyleIds?: IDefaultStyleMap;
    onSetDefaultStyle?: (elementType: StorefrontStyleElementType, styleId: string | undefined) => void;
    /**
     * The pages editor's WORKING settings, for the palette editor's "Scan storefront colors"
     * helper: it is both the scan source (typed style fields + page-config blobs) and one of the
     * write targets. Rewrites come back through `onWorkingSettingsChange` as an ordinary draft edit
     * - the same channel "Apply to all" uses - so they persist on Exit Edit Mode. Omit both to hide
     * the helper.
     */
    workingSettings?: IStorefrontSettings;
    onWorkingSettingsChange?: (settings: IStorefrontSettings) => void;
}

// ── Mini previews ────────────────────────────────────────────────────────────────────────
// Rendered from the style's own parts through the same builders the page previews use, so a
// swatch looks like the real thing (fontSize is a percent, `font` maps to fontFamily, …).

// A part is an untyped camelCase CSS dict; the builders want the (slightly narrower) config
// interfaces. One cast, used everywhere a part feeds a builder.
const asStyle = (part?: { [k: string]: any }): any => part;

// ── Token docs (palette / font pack) ─────────────────────────────────────────────────────
// These two carry a single slot dictionary each rather than CSS parts, so everything about them -
// the list row, the editor body, the actions - is bespoke rather than the generic part-group flow.

const paletteColorsOf = (parts?: IStorefrontStyleParts): Record<string, string> =>
    (parts?.colors ?? {}) as Record<string, string>;

const fontPackFontsOf = (parts?: IStorefrontStyleParts): Record<string, string> =>
    (parts?.fonts ?? {}) as Record<string, string>;

// The Squarespace-style strip: one chip per character-carrying slot, butted together so a palette
// reads as a single object at a glance rather than as six separate swatches.
const PaletteStrip: React.FC<{ parts?: IStorefrontStyleParts; size?: 'sm' | 'lg' }> = ({ parts, size = 'sm' }) => {
    const colors = paletteColorsOf(parts);
    const chip = size === 'lg' ? 40 : 22;
    return (
        <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', border: '1px solid #edebe9', flexShrink: 0 }}>
            {PALETTE_STRIP_SLOTS.map((slot) => (
                <span
                    key={slot}
                    title={`${PALETTE_SLOT_LABELS[slot]}${colors[slot] ? ` - ${colors[slot]}` : ''}`}
                    style={{
                        width: chip,
                        height: chip,
                        // An unset slot shows as a light neutral rather than transparent, so the
                        // strip keeps its shape and the gap is visible as a gap.
                        background: colors[slot] || '#faf9f8',
                    }}
                />
            ))}
        </div>
    );
};

// The "Aa" tile: the pack's own heading font over a line of its body font, so the pairing is the
// thing being judged rather than two font names.
const FontPackTile: React.FC<{ parts?: IStorefrontStyleParts; size?: 'sm' | 'lg' }> = ({ parts, size = 'sm' }) => {
    const fonts = fontPackFontsOf(parts);
    const large = size === 'lg';
    return (
        <div style={{
            width: large ? '100%' : 128,
            minWidth: large ? undefined : 128,
            height: large ? 120 : 56,
            boxSizing: 'border-box',
            borderRadius: 4,
            border: '1px solid #edebe9',
            background: '#faf9f8',
            padding: large ? 12 : 6,
            overflow: 'hidden',
            textAlign: 'left',
        }}>
            <div style={{ fontFamily: fonts.heading || undefined, fontSize: large ? 26 : 15, lineHeight: 1.15, fontWeight: 600 }}>
                Aa Heading
            </div>
            <div style={{ fontFamily: fonts.body || undefined, fontSize: large ? 13 : 9, lineHeight: 1.3, marginTop: large ? 6 : 2, color: '#605e5c' }}>
                {large
                    ? 'The quick brown fox jumps over the lazy dog. Body copy sets the tone of every product description, price and paragraph on the storefront.'
                    : 'The quick brown fox jumps over the lazy dog.'}
            </div>
        </div>
    );
};

const StyleSwatch: React.FC<{
    elementType: StorefrontStyleElementType;
    parts?: IStorefrontStyleParts;
    /** 'lg' is the hero preview at the top of the style editor. */
    size?: 'sm' | 'lg';
}> = ({ elementType, parts, size = 'sm' }) => {
    const large = size === 'lg';
    // The token docs get their own tiles rather than an abstract element preview: a palette IS its
    // colours and a font pack IS its type, so there is nothing to stand in for.
    if (elementType === 'palette') {
        return (
            <div style={{ display: 'flex', alignItems: 'center', width: large ? '100%' : undefined }}>
                <PaletteStrip parts={parts} size={size} />
            </div>
        );
    }
    if (elementType === 'fontPack') return <FontPackTile parts={parts} size={size} />;
    const frame: React.CSSProperties = {
        width: large ? '100%' : 128,
        minWidth: large ? undefined : 128,
        height: large ? 120 : 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        borderRadius: 4,
        border: '1px solid #edebe9',
        background: '#faf9f8',
        padding: large ? 12 : 4,
        boxSizing: 'border-box',
    };
    const scale = large ? 1.6 : 1;
    const px = (n: number) => Math.round(n * scale);
    const p = parts ?? {};
    let body: React.ReactNode = null;
    switch (elementType) {
        case 'button':
            body = (
                <Stack horizontal tokens={{ childrenGap: px(4) }} verticalAlign='center'>
                    <span style={{ display: 'inline-block', padding: `${px(4)}px ${px(10)}px`, borderRadius: 4, background: '#0f6cbd', color: '#fff', fontSize: px(11), ...elementStyleFrom(asStyle(p.base), 'base') }}>Button</span>
                    {p.label && Object.keys(p.label).length > 0 && (
                        <span style={{ display: 'inline-block', padding: `${px(2)}px ${px(6)}px`, borderRadius: 10, background: '#fff', fontSize: px(10), ...elementStyleFrom(asStyle(p.label), 'label') }}>Label</span>
                    )}
                </Stack>
            );
            break;
        case 'card':
            body = (
                <div style={{ width: px(84), background: '#fff', border: '1px solid #e1dfdd', borderRadius: 4, padding: px(4), boxSizing: 'border-box', ...cardStyleFrom(asStyle(p.card), 'card') }}>
                    <div style={{ height: px(14), background: '#f3f2f1', borderRadius: 2, marginBottom: px(3), ...elementStyleFrom(asStyle(p.image), 'image') }} />
                    <div style={{ fontSize: px(9), lineHeight: 1.1, ...textStyleFrom(asStyle(p.title), 'title') }}>Product</div>
                    <div style={{ fontSize: px(9), fontWeight: 600, ...priceStyleFrom(asStyle(p.price), 'price') }}>$99</div>
                </div>
            );
            break;
        case 'section':
            body = (
                <div style={{ width: '100%', height: '100%', background: '#fff', border: '1px solid #e1dfdd', borderRadius: 3, boxSizing: 'border-box', ...elementStyleFrom(asStyle(p.band), 'band') }}>
                    <div style={{ fontSize: px(9), padding: `${px(2)}px ${px(4)}px`, borderBottom: '1px solid #f3f2f1', ...elementStyleFrom(asStyle(p.title), 'title') }}>Section Title</div>
                    <div style={{ display: 'flex', gap: px(3), padding: px(4) }}>
                        {[0, 1, 2].map((i) => <div key={i} style={{ flex: 1, height: px(18), background: '#f3f2f1', borderRadius: 2, ...elementStyleFrom(asStyle(p.product), 'product') }} />)}
                    </div>
                </div>
            );
            break;
        case 'typography':
            body = (
                <div style={{ width: '100%', textAlign: 'left' }}>
                    <div style={{ fontSize: px(12), fontWeight: 600, lineHeight: 1.2, ...textStyleFrom(asStyle(p.heading), 'heading') }}>Heading</div>
                    <div style={{ fontSize: px(10), lineHeight: 1.2, ...textStyleFrom(asStyle(p.body), 'body') }}>Body text</div>
                </div>
            );
            break;
        case 'form':
            body = (
                <div style={{ width: '100%', textAlign: 'left' }}>
                    <div style={{ fontSize: px(9), marginBottom: px(2), ...textStyleFrom(asStyle(p.label), 'label') }}>Label</div>
                    <div style={{ height: px(18), background: '#fff', border: '1px solid #8a8886', borderRadius: 2, ...elementStyleFrom(asStyle(p.input), 'input') }} />
                </div>
            );
            break;
        default:
            body = <span style={{ fontSize: 11, color: '#605e5c' }}>Preview</span>;
    }
    return <div style={frame}>{body}</div>;
};

// The flat-row chrome (cell, row, colour row, group label, text-align group) is shared with the
// section editor's Appearance tab — see styleRows.tsx — and the value-shaped rows this panel writes
// into a part dict now live in styleControls.tsx as ElementPropertyRows, shared with the section
// editor's card pencil popover.

const ScopeBadge: React.FC<{ style: IStorefrontStyle }> = ({ style }) => (
    isGlobalStyle(style)
        ? <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: '#e2e9cb', color: '#43571a', whiteSpace: 'nowrap' }}>Global</span>
        : null
);

// The one palette / font pack the storefront is currently resolving its `$palette:` / `$font:`
// tokens against. Exactly one per type can carry it; the activate endpoint clears the siblings.
const ActiveBadge: React.FC = () => (
    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: '#e2e9cb', color: '#43571a', whiteSpace: 'nowrap' }}>
        <Icon iconName='CheckMark' style={{ fontSize: 9, marginRight: 3, verticalAlign: 'baseline' }} />
        Active
    </span>
);

// Marks the style new sections seed their $styleRefs from (styleConfig defaultStyles).
const DefaultBadge: React.FC = () => (
    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: '#f9fbf4', border: '1px solid #a8c371', color: '#43571a', whiteSpace: 'nowrap' }}>
        <Icon iconName='FavoriteStarFill' style={{ fontSize: 8, marginRight: 3, verticalAlign: 'baseline' }} />
        Default
    </span>
);

// The failure line of the loading toast needs the plain verb behind the in-flight gerund. Stripping
// "-ing" alone loses the silent e it swallowed ("Saving" -> "sav"), which used to read as
// "Failed to sav Primary", so the handful of verbs this panel uses are spelled out.
const INFINITIVE: Record<string, string> = {
    Saving: 'save',
    Creating: 'create',
    Duplicating: 'duplicate',
    Renaming: 'rename',
    Deleting: 'delete',
    Promoting: 'promote',
    Activating: 'activate',
    Adding: 'add',
};

// The panel's view stack. `style` always edits whatever is in `draft`, so the id lives there.
type PanelView =
    | { level: 'root' }
    | { level: 'category'; elementType: StorefrontStyleElementType }
    | { level: 'style'; elementType: StorefrontStyleElementType };

const StorefrontStylesPanel: React.FC<IStorefrontStylesPanelProps> = (props) => {
    const { storefront, styles, onDraftChange } = props;
    const theme = useTheme();
    const [saveStyle] = useSaveStorefrontStyleMutation();
    const [deleteStyle] = useDeleteStorefrontStyleMutation();
    const [promoteStyle] = usePromoteStorefrontStyleMutation();
    const [activateStyle] = useActivateStorefrontStyleMutation();
    const [seedStyles] = useSeedStorefrontStylesMutation();
    const cell = useCellControlStyles();

    const [view, setView] = React.useState<PanelView>({ level: 'root' });
    // The style open in the drill-in editor. Held as a full copy so the page preview can render it
    // (via onDraftChange) before it is saved; Cancel simply throws it away.
    const [draft, setDraft] = React.useState<IStorefrontStyle | undefined>();
    const [expandedPart, setExpandedPart] = React.useState<string | undefined>();
    const [advancedPart, setAdvancedPart] = React.useState<string | undefined>();
    const [busy, setBusy] = React.useState(false);
    const [renameTarget, setRenameTarget] = React.useState<IStorefrontStyle | undefined>();
    const [renameValue, setRenameValue] = React.useState('');
    const [deleteTarget, setDeleteTarget] = React.useState<IStorefrontStyle | undefined>();
    const [promoteTarget, setPromoteTarget] = React.useState<IStorefrontStyle | undefined>();
    const [confirmGlobalSave, setConfirmGlobalSave] = React.useState(false);
    const [applyAllTarget, setApplyAllTarget] = React.useState<IStorefrontStyle | undefined>();
    const [tokenizeOpen, setTokenizeOpen] = React.useState(false);

    React.useEffect(() => { onDraftChange(draft); }, [draft, onDraftChange]);
    // Closing the panel abandons any in-progress edit rather than leaving a phantom preview behind.
    React.useEffect(() => { if (!props.isOpen) { setDraft(undefined); setView({ level: 'root' }); } }, [props.isOpen]);

    const byType = React.useMemo(() => {
        const map = {} as Record<StorefrontStyleElementType, IStorefrontStyle[]>;
        for (const t of STYLE_ELEMENT_TYPES) map[t] = sortedStyles(styles.filter((s) => s.elementType === t));
        return map;
    }, [styles]);

    // ── Mutations, each wrapped in the house loading→resolve toast ───────────────────────
    // `gerund` drives the in-flight text ("Saving X…"), `past` the resolution ("X saved").
    // `action` is left untyped because the mutations don't share a response shape: most resolve to
    // an ActionResponse, while seed resolves to the collection of styles it created. Only the
    // ActionResponse-ish fields are read, and only when present.
    type MutationResult = { success?: boolean; message?: string; id?: string } | null | undefined;
    const run = async (objectName: string, gerund: string, past: string, action: () => Promise<unknown>): Promise<string | undefined> => {
        if (busy) return undefined;
        setBusy(true);
        const failure = `Failed to ${INFINITIVE[gerund] ?? gerund.replace(/ing$/, '').toLowerCase()} ${objectName}`;
        const toastId = toast.loading(`${gerund} ${objectName}…`);
        try {
            const result = await action() as MutationResult;
            if (result && result.success === false) {
                toast.update(toastId, { type: 'error', render: result.message || failure, isLoading: false, autoClose: 4000 });
                return undefined;
            }
            toast.update(toastId, { type: 'success', render: `${objectName} ${past}`, isLoading: false, autoClose: 3000 });
            return result?.id ?? '';
        } catch {
            toast.update(toastId, { type: 'error', render: failure, isLoading: false, autoClose: 4000 });
            return undefined;
        } finally {
            setBusy(false);
        }
    };

    // Drill into the STYLE level on a copy of the style, with its first part open.
    const openStyle = (style: IStorefrontStyle) => {
        setDraft({ ...style, parts: JSON.parse(JSON.stringify(style.parts ?? {})) });
        setExpandedPart(STYLE_PARTS[style.elementType]?.[0]);
        setView({ level: 'style', elementType: style.elementType });
    };

    // Leaving the style level always discards the draft — same as Cancel — so the page preview
    // never keeps showing an edit the merchant walked away from.
    const closeStyle = () => {
        const elementType = view.level === 'root' ? undefined : view.elementType;
        setDraft(undefined);
        setAdvancedPart(undefined);
        setView(elementType ? { level: 'category', elementType } : { level: 'root' });
    };

    const createStyle = async (elementType: StorefrontStyleElementType) => {
        // "New Color Palette", not "New Color Palette Style" - a palette isn't a style of anything.
        const name = isTokenElementType(elementType)
            ? `New ${STYLE_ELEMENT_TYPE_SINGULAR[elementType]}`
            : `New ${STYLE_ELEMENT_TYPE_SINGULAR[elementType]} Style`;
        const body: IStorefrontStyleSaveRequest = {
            name,
            elementType,
            storefront,
            sortOrder: nextSortOrder(styles, elementType),
            parts: emptyPartsFor(elementType),
        };
        const id = await run(name, 'Creating', 'created', () => saveStyle({ style: body, storefront }).unwrap());
        // Drill straight into the editor on the freshly-created doc (the list refetch will bring the
        // identical server copy; the draft simply lets the merchant start editing immediately).
        if (id) openStyle({ ...body, id } as IStorefrontStyle);
    };

    const duplicateStyle = async (style: IStorefrontStyle) => {
        const name = `${style.name} Copy`;
        const body: IStorefrontStyleSaveRequest = {
            name,
            elementType: style.elementType,
            // A duplicate always lands in THIS storefront's scope, even when duplicating a global.
            storefront,
            sortOrder: nextSortOrder(styles, style.elementType),
            parts: JSON.parse(JSON.stringify(style.parts ?? {})),
        };
        await run(name, 'Duplicating', 'created', () => saveStyle({ style: body, storefront }).unwrap());
    };

    const commitRename = async () => {
        const target = renameTarget;
        const name = renameValue.trim();
        setRenameTarget(undefined);
        if (!target || !name || name === target.name) return;
        await run(name, 'Renaming', 'renamed', () => saveStyle({ style: { ...target, name }, storefront }).unwrap());
        setDraft((d) => (d && d.id === target.id ? { ...d, name } : d));
    };

    const commitDelete = async () => {
        const target = deleteTarget;
        setDeleteTarget(undefined);
        if (!target) return;
        await run(target.name, 'Deleting', 'deleted', () => deleteStyle({ id: target.id, storefront }).unwrap());
        // A deleted style can't stay the default for new sections.
        if (props.defaultStyleIds?.[target.elementType] === target.id) props.onSetDefaultStyle?.(target.elementType, undefined);
        if (draft && draft.id === target.id) closeStyle();
    };

    const commitPromote = async () => {
        const target = promoteTarget;
        setPromoteTarget(undefined);
        if (!target) return;
        await run(target.name, 'Promoting', 'moved to Global', () => promoteStyle({ id: target.id, storefront }).unwrap());
        // The id is preserved by design, so every existing $styleRef keeps working — just re-scope
        // any in-progress draft so the editor's global warning appears.
        setDraft((d) => (d && d.id === target.id ? { ...d, storefront: 'global' } : d));
    };

    // Make this the palette / font pack the storefront resolves its tokens against. One API call -
    // the server clears every same-type sibling - and the invalidated list brings the new flags
    // back, which republishes the registry and repaints every tokenized value in the preview.
    const setActive = async (style: IStorefrontStyle) => {
        await run(style.name, 'Activating', 'is now active', () => activateStyle({ id: style.id, storefront }).unwrap());
        // Reflect it on an open draft too, so the editor's Active state doesn't lag the list.
        setDraft((d) => (d && d.id === style.id ? { ...d, isActive: true } : d));
    };

    // Why a doc can't be activated, or undefined when it can. Mirrors the API's own rule so the
    // affordance is disabled with an explanation rather than failing on click.
    const activationBlockedReason = (style: IStorefrontStyle): string | undefined => {
        if (isGlobalStyle(style)) return 'Activation is per-storefront. Duplicate this into the storefront to make it active here.';
        if (style.isActive === true) return `Already the active ${STYLE_ELEMENT_TYPE_SINGULAR[style.elementType].toLowerCase()} for this storefront.`;
        return undefined;
    };

    const runSeed = async () => {
        await run('starter styles', 'Adding', 'added', () => seedStyles({ storefront }).unwrap());
    };

    const saveDraft = async () => {
        if (!draft) return;
        await run(draft.name, 'Saving', 'saved', () => saveStyle({ style: draft, storefront }).unwrap());
        // Drop the draft either way: on success the refetched server copy takes over, and on
        // failure the toast already said so — keeping a phantom draft in the preview would lie.
        closeStyle();
    };

    const onSaveClicked = () => {
        // Global styles bypass preview and apply org-wide the moment they're saved — confirm first.
        if (draft && isGlobalStyle(draft)) setConfirmGlobalSave(true);
        else void saveDraft();
    };

    // "Apply to all buttons/product cards/…": stamps this style's $styleRef onto every element key
    // of its type across the storefront's page-config drafts. Local overrides on those elements are
    // preserved. It's a draft edit like any other in Edit Mode — it persists on Exit Edit Mode, not
    // through an API call of its own. Hidden when the host didn't wire the callbacks, or when the
    // element type has no config-level keys (section / typography today).
    const bulkApplyPlanFor = (elementType: StorefrontStyleElementType): IStyleBulkApplyPlanEntry[] =>
        (props.styleBulkApplyPlan && props.onApplyStyleToAll) ? props.styleBulkApplyPlan(elementType) : [];

    // "Scan storefront colors": rewrite the literal colours the merchant mapped into
    // `$palette:<slot>` tokens. Three write paths, each on its own existing mechanism - the settings
    // + page-config rewrites are a working-draft edit (persist on Exit Edit Mode), the named styles
    // are saved through the styles API here and now. See paletteTokenize.ts.
    const commitTokenize = async (mapping: IColorTokenMapping) => {
        const settings = props.workingSettings;
        const onSettingsChange = props.onWorkingSettingsChange;
        if (!settings || !onSettingsChange || busy) return;
        setTokenizeOpen(false);
        const result = applyColorTokens(settings, styles, mapping);
        if (result.total === 0) {
            toast.info('None of those colors are still written out by hand - nothing changed');
            return;
        }
        setBusy(true);
        const toastId = toast.loading('Linking colors to the palette…');
        try {
            if (result.settings && result.settings !== settings) onSettingsChange(result.settings);
            // One save per changed style doc; a failure aborts and the toast says so, leaving the
            // already-saved docs tokenized (they are valid on their own - a token is just a value).
            for (const style of result.changedStyles) {
                await saveStyle({ style, storefront }).unwrap();
            }
            const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;
            const parts = [
                result.settingsFields > 0 ? plural(result.settingsFields, 'storefront color') : undefined,
                result.configValues > 0 ? plural(result.configValues, 'page value') : undefined,
                result.styleValues > 0 ? plural(result.styleValues, 'style value') : undefined,
            ].filter(Boolean);
            toast.update(toastId, {
                type: 'success',
                render: `Linked ${parts.join(', ')} to ${draft?.name ?? 'the palette'}${result.settingsFields + result.configValues > 0 ? ' - exit Edit Mode to publish the page changes' : ''}`,
                isLoading: false,
                autoClose: 5000,
            });
        } catch {
            toast.update(toastId, { type: 'error', render: 'Failed to link colors to the palette', isLoading: false, autoClose: 4000 });
        } finally {
            setBusy(false);
        }
    };

    const commitApplyAll = () => {
        const target = applyAllTarget;
        setApplyAllTarget(undefined);
        if (!target || !props.onApplyStyleToAll) return;
        const changed = props.onApplyStyleToAll(target);
        toast.success(changed > 0
            ? `Applied ${target.name} to ${changed} element${changed === 1 ? '' : 's'} — save to publish`
            : `Every matching element already uses ${target.name}`);
    };

    // Set/clear this style as the storefront's default for its element type — a working-settings
    // draft edit (persists via the pages editor's save), used to seed new sections' $styleRefs.
    const isDefaultStyle = (style: IStorefrontStyle): boolean => props.defaultStyleIds?.[style.elementType] === style.id;
    const setDefault = (style: IStorefrontStyle, makeDefault: boolean) => {
        props.onSetDefaultStyle?.(style.elementType, makeDefault ? style.id : undefined);
        toast.success(makeDefault
            ? `${style.name} is now the default ${STYLE_ELEMENT_TYPE_SINGULAR[style.elementType]} style — new sections will use it. Save to publish.`
            : `Cleared the default ${STYLE_ELEMENT_TYPE_SINGULAR[style.elementType]} style — save to publish`);
    };

    const menuItems = (style: IStorefrontStyle): IContextualMenuItem[] => [
        { key: 'edit', text: 'Edit', iconProps: { iconName: 'Edit' }, onClick: () => openStyle(style) },
        // Palettes / font packs: Set Active takes the place of the default-style + apply-to-all
        // items, neither of which means anything for a doc nothing references by id.
        ...(isTokenElementType(style.elementType)
            ? [{
                key: 'setActive',
                text: 'Set Active',
                iconProps: { iconName: 'CheckMark' },
                disabled: !!activationBlockedReason(style),
                title: activationBlockedReason(style),
                onClick: () => { void setActive(style); },
            }]
            : []),
        { key: 'rename', text: 'Rename', iconProps: { iconName: 'Rename' }, onClick: () => { setRenameTarget(style); setRenameValue(style.name); } },
        { key: 'duplicate', text: 'Duplicate', iconProps: { iconName: 'Copy' }, onClick: () => { void duplicateStyle(style); } },
        ...(!isTokenElementType(style.elementType) && props.onSetDefaultStyle && props.defaultStyleIds
            ? [isDefaultStyle(style)
                ? { key: 'clearDefault', text: 'Clear default', iconProps: { iconName: 'Unfavorite' }, onClick: () => setDefault(style, false) }
                : { key: 'setDefault', text: `Set as default ${STYLE_ELEMENT_TYPE_SINGULAR[style.elementType].toLowerCase()} style`, iconProps: { iconName: 'FavoriteStar' }, onClick: () => setDefault(style, true) }]
            : []),
        ...(bulkApplyPlanFor(style.elementType).length > 0
            ? [{
                key: 'applyAll',
                text: `Apply to all ${STYLE_ELEMENT_TYPE_LABELS[style.elementType].toLowerCase()}…`,
                iconProps: { iconName: 'BulletedList' },
                onClick: () => setApplyAllTarget(style),
            }]
            : []),
        ...(isGlobalStyle(style) ? [] : [{ key: 'promote', text: 'Move to Global…', iconProps: { iconName: 'Globe' }, onClick: () => setPromoteTarget(style) }]),
        { key: 'delete', text: 'Delete…', iconProps: { iconName: 'Delete' }, onClick: () => setDeleteTarget(style) },
    ];

    const backHeader = (text: string, onClick: () => void) => (
        <ActionButton
            iconProps={{ iconName: 'ChevronLeft' }}
            text={text}
            disabled={busy}
            onClick={onClick}
            styles={{ root: { paddingLeft: 0, height: 28 }, label: { fontSize: 13 } }}
        />
    );

    const rowShell: React.CSSProperties = {
        padding: 6,
        border: `1px solid ${theme.palette.neutralLight}`,
        borderRadius: 4,
        background: theme.palette.white,
    };

    // ── ROOT: one row per category ───────────────────────────────────────────────────────
    const renderRoot = () => (
        <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, color: theme.palette.neutralSecondary, marginBottom: '1em' }}>
                Named styles you can attach to any element. Editing a style updates every element that uses it;
                an element's own settings always win over the style.
            </div>
            <Stack tokens={{ childrenGap: 8 }}>
                {STYLE_ELEMENT_TYPES.map((elementType) => {
                    const list = byType[elementType] ?? [];
                    const globals = list.filter(isGlobalStyle).length;
                    const isToken = isTokenElementType(elementType);
                    // A token category previews the ACTIVE doc - that is the one the storefront is
                    // actually painting with; for the preset categories the first is as good as any.
                    const active = isToken ? list.find((s) => s.isActive === true) : undefined;
                    const previewParts = (active ?? list[0])?.parts;
                    const noun = isToken ? STYLE_ELEMENT_TYPE_SINGULAR[elementType].toLowerCase() : 'style';
                    return (
                        <div
                            key={elementType}
                            role='button'
                            tabIndex={0}
                            onClick={() => setView({ level: 'category', elementType })}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setView({ level: 'category', elementType }); }}
                            style={{ ...rowShell, cursor: 'pointer' }}
                        >
                            <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 8 }}>
                                <StyleSwatch elementType={elementType} parts={previewParts} />
                                <Stack grow style={{ minWidth: 0 }}>
                                    <span style={{ fontWeight: 600, fontSize: 13 }}>{STYLE_ELEMENT_TYPE_LABELS[elementType]}</span>
                                    <span style={{ fontSize: 11, color: theme.palette.neutralSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {list.length === 0 ? `No ${noun}s yet` : `${list.length} ${noun}${list.length === 1 ? '' : 's'}`}
                                        {globals > 0 ? ` · ${globals} global` : ''}
                                        {active ? ` · ${active.name} active` : (isToken && list.length > 0 ? ' · none active' : '')}
                                    </span>
                                </Stack>
                                <Icon iconName='ChevronRight' style={{ fontSize: 12, color: theme.palette.neutralSecondary }} />
                            </Stack>
                        </div>
                    );
                })}
            </Stack>
        </div>
    );

    // ── CATEGORY: the styles of one element type ─────────────────────────────────────────
    const renderCategory = (elementType: StorefrontStyleElementType) => {
        const list = byType[elementType] ?? [];
        const isToken = isTokenElementType(elementType);
        const singular = STYLE_ELEMENT_TYPE_SINGULAR[elementType].toLowerCase();
        return (
            <div style={{ marginTop: 8 }}>
                {backHeader('Storefront Styles', () => setView({ level: 'root' }))}
                <Stack horizontal horizontalAlign='space-between' verticalAlign='center' style={{ marginBottom: '0.5em' }}>
                    <h2 style={{ margin: 0, fontSize: 18 }}>{STYLE_ELEMENT_TYPE_LABELS[elementType]}</h2>
                    <DefaultButton text={isToken ? `New ${singular}` : 'New style'} iconProps={{ iconName: 'Add' }} disabled={busy} onClick={() => { void createStyle(elementType); }} />
                </Stack>
                {isToken && (
                    <div style={{ fontSize: 12, color: theme.palette.neutralSecondary, marginBottom: '0.5em' }}>
                        {elementType === 'palette'
                            ? 'One palette is active at a time. Colors anywhere on the storefront can point at a palette slot instead of a fixed value, and then follow it.'
                            : 'One font pack is active at a time. Fonts anywhere on the storefront can point at its heading or body slot and then follow it.'}
                    </div>
                )}
                {list.length === 0 ? (
                    <div style={{ padding: '1em', border: `1px dashed ${theme.palette.neutralTertiaryAlt}`, borderRadius: 4, fontSize: 12, color: theme.palette.neutralSecondary }}>
                        <div style={{ marginBottom: '0.5em' }}>
                            No {isToken ? `${singular}s` : `${STYLE_ELEMENT_TYPE_LABELS[elementType].toLowerCase()} styles`} yet.
                        </div>
                        {/* The seeder only produces the five preset types, so a token category
                            offers the plain create instead of "Add starter styles". */}
                        {isToken
                            ? <DefaultButton text={`New ${singular}`} iconProps={{ iconName: 'Add' }} disabled={busy} onClick={() => { void createStyle(elementType); }} />
                            : <DefaultButton text='Add starter styles' iconProps={{ iconName: 'Lightbulb' }} disabled={busy} onClick={() => { void runSeed(); }} />}
                    </div>
                ) : (
                    <Stack tokens={{ childrenGap: 8 }}>
                        {list.map((style) => (
                            <div
                                key={style.id}
                                role='button'
                                tabIndex={0}
                                onClick={() => openStyle(style)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openStyle(style); }}
                                style={{ ...rowShell, cursor: 'pointer' }}
                            >
                                <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 8 }}>
                                    <StyleSwatch elementType={style.elementType} parts={style.parts} />
                                    <Stack grow style={{ minWidth: 0 }}>
                                        <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{style.name}</span>
                                        {(isGlobalStyle(style) || isDefaultStyle(style) || style.isActive === true) && (
                                            <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 6 }} style={{ marginTop: 2 }}>
                                                <ScopeBadge style={style} />
                                                {style.isActive === true && <ActiveBadge />}
                                                {isDefaultStyle(style) && <DefaultBadge />}
                                            </Stack>
                                        )}
                                    </Stack>
                                    <IconButton
                                        iconProps={{ iconName: 'More' }}
                                        title={`${style.name} actions`}
                                        ariaLabel={`${style.name} actions`}
                                        disabled={busy}
                                        menuProps={{ items: menuItems(style) }}
                                        // The row itself opens the editor; the menu must not also trigger that.
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </Stack>
                            </div>
                        ))}
                    </Stack>
                )}
            </div>
        );
    };

    // ── STYLE: hero preview + flat rows per part ─────────────────────────────────────────
    const updatePart = (part: string, patch: Partial<ISearchElementStyle>) => {
        if (!draft) return;
        const current = (draft.parts?.[part] ?? {}) as Record<string, unknown>;
        const merged: Record<string, unknown> = { ...current, ...patch };
        // Cleared controls come through as undefined/''; drop them so the part stays a tidy CSS dict.
        for (const key of Object.keys(merged)) if (merged[key] === undefined || merged[key] === '') delete merged[key];
        setDraft({ ...draft, parts: { ...(draft.parts ?? {}), [part]: merged } });
    };

    // A token doc's single slot dictionary (`colors` / `fonts`). Clearing a slot removes the key
    // rather than storing '', so the token simply stops resolving and the property drops - the same
    // thing the renderer does with an unknown slot.
    const updateSlot = (part: 'colors' | 'fonts', slot: string, value?: string) => {
        if (!draft) return;
        const current = { ...((draft.parts?.[part] ?? {}) as Record<string, unknown>) };
        if (value === undefined || value === '') delete current[slot];
        else current[slot] = value;
        setDraft({ ...draft, parts: { ...(draft.parts ?? {}), [part]: current } });
    };

    // The Advanced editor hands back the whole dict — same target, so the two paths interleave.
    const replacePart = (part: string, css: Css) => {
        if (!draft) return;
        setDraft({ ...draft, parts: { ...(draft.parts ?? {}), [part]: { ...(css as Record<string, any>) } } });
    };

    // Product-card styles get the real card as their hero instead of the abstract swatch: the parts
    // of a card style map one-to-one onto <ProductCard>'s slots, so there is no reason to show a
    // stand-in. Built through the SAME builders the page previews use, straight off `draft.parts`,
    // so it repaints on every keystroke exactly as the swatch did. Every other element type keeps
    // StyleSwatch — there is no single "real" component behind a typography or form style — and the
    // small swatches in the root/category lists are unchanged everywhere.
    const renderHero = () => {
        if (!draft) return null;
        if (draft.elementType !== 'card') return <StyleSwatch elementType={draft.elementType} parts={draft.parts} size='lg' />;
        const p = draft.parts ?? {};
        return (
            // Sticky while the part groups scroll, on viewports tall enough to afford it
            // (sf-card-example-sticky, StorefrontEditor.css) — same as ProductCardStylePanel.
            <div className='sf-card-example-sticky'>
                <ProductCardExample
                    cardStyles={{
                        card: cardStyleFrom(asStyle(p.card), 'card'),
                        title: textStyleFrom(asStyle(p.title), 'title'),
                        price: priceStyleFrom(asStyle(p.price), 'price'),
                        image: elementStyleFrom(asStyle(p.image), 'image'),
                    }}
                    hoverStyle={elementStyleFrom(asStyle(p.hover), 'hover')}
                />
            </div>
        );
    };

    // ── STYLE (token docs): the palette / font pack editors ──────────────────────────────
    // Bespoke rather than the part-group surface: there are no CSS properties to edit here, just a
    // fixed set of named slots. Everything else - the draft, the live onDraftChange preview chain,
    // the Save/Cancel footer - is the panel's normal flow, so a palette edit repaints the page
    // preview on every keystroke exactly as a button style does.
    const renderTokenEditor = (elementType: StorefrontStyleElementType) => {
        if (!draft) return null;
        const isPalette = elementType === 'palette';
        const colors = paletteColorsOf(draft.parts);
        const fonts = fontPackFontsOf(draft.parts);
        const blocked = activationBlockedReason(draft);
        const canTokenize = isPalette && !!props.workingSettings && !!props.onWorkingSettingsChange;
        return (
            <div style={{ marginTop: 8 }}>
                {backHeader(STYLE_ELEMENT_TYPE_LABELS[elementType], closeStyle)}
                {isGlobalStyle(draft) && (
                    <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginBottom: '0.5em' } }}>
                        This is a Global {STYLE_ELEMENT_TYPE_SINGULAR[elementType]} - edits apply to all storefronts in your
                        organization, immediately. It can&apos;t be made active for one storefront; duplicate it here first.
                    </MessageBar>
                )}
                {/* Hero preview - repaints from the draft on every keystroke. */}
                <StyleSwatch elementType={elementType} parts={draft.parts} size='lg' />
                <div style={{ marginTop: '0.5em' }}>
                    <TextField label='Name' value={draft.name} onChange={(_, v) => setDraft({ ...draft, name: v ?? '' })} />
                </div>
                <Stack horizontal wrap verticalAlign='center' tokens={{ childrenGap: '0.5em' }} style={{ marginTop: '0.5em', marginBottom: '1em' }}>
                    {draft.isActive === true
                        ? (
                            <>
                                <ActiveBadge />
                                <span style={{ fontSize: 11, color: theme.palette.neutralSecondary }}>
                                    Every {isPalette ? '$palette' : '$font'} value on this storefront resolves from here.
                                </span>
                            </>
                        )
                        : (
                            <DefaultButton
                                text='Set Active'
                                iconProps={{ iconName: 'CheckMark' }}
                                disabled={busy || !!blocked}
                                title={blocked ?? `Make this the ${STYLE_ELEMENT_TYPE_SINGULAR[elementType].toLowerCase()} this storefront resolves its tokens from`}
                                onClick={() => { void setActive(draft); }}
                            />
                        )}
                    {canTokenize && (
                        <DefaultButton
                            text='Scan storefront colors…'
                            iconProps={{ iconName: 'Color' }}
                            disabled={busy}
                            title='Find colors still written out by hand and point them at this palette'
                            onClick={() => setTokenizeOpen(true)}
                        />
                    )}
                </Stack>
                {isPalette
                    ? PALETTE_SLOTS.map((slot: PaletteSlot) => (
                        <ColorRow
                            key={slot}
                            label={PALETTE_SLOT_LABELS[slot]}
                            value={colors[slot]}
                            onChange={(v) => updateSlot('colors', slot, v)}
                        />
                    ))
                    : FONT_SLOTS.map((slot: FontSlot) => (
                        <StyleRow key={slot} label={FONT_SLOT_LABELS[slot]}>
                            <FontPicker
                                selectedFont={fonts[slot] ?? ''}
                                placeholder='Default'
                                onChange={(f) => updateSlot('fonts', slot, f)}
                                styles={cell.dropdown}
                            />
                        </StyleRow>
                    ))}
            </div>
        );
    };

    const renderStyleEditor = (elementType: StorefrontStyleElementType) => {
        if (!draft) return null;
        if (isTokenElementType(elementType)) return renderTokenEditor(elementType);
        const parts = STYLE_PARTS[elementType] ?? [];
        return (
            <div style={{ marginTop: 8 }}>
                {backHeader(STYLE_ELEMENT_TYPE_LABELS[elementType], closeStyle)}
                {isGlobalStyle(draft) && (
                    <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginBottom: '0.5em' } }}>
                        This is a Global Style — edits apply to all storefronts in your organization, immediately (global styles bypass preview).
                    </MessageBar>
                )}
                {/* Hero preview — repaints from the draft on every keystroke. */}
                {renderHero()}
                <div style={{ marginTop: '0.5em', marginBottom: '1em' }}>
                    <TextField label='Name' value={draft.name} onChange={(_, v) => setDraft({ ...draft, name: v ?? '' })} />
                </div>
                {parts.map((part) => (
                    <StyleGroupAccordion
                        key={part}
                        label={STYLE_PART_LABELS[part] ?? part}
                        iconName={STYLE_PART_ICONS[part]}
                        expanded={expandedPart === part}
                        // One part open at a time, so the surface stays scannable.
                        onToggle={(isExpanded) => setExpandedPart(isExpanded ? part : undefined)}
                    >
                        <ElementPropertyRows settingKeyPrefix={`storefront.namedStyle.${part}`}
                            value={draft.parts?.[part] as ISearchElementStyle | undefined}
                            onChange={(patch) => updatePart(part, patch)}
                        />
                        {/* Centered so it reads as an action, not another left-aligned group header
                            (design feedback: left-aligned with an icon, it kept being mistaken for one). */}
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <ActionButton
                                iconProps={{ iconName: 'Settings' }}
                                text='Advanced…'
                                onClick={() => setAdvancedPart(part)}
                                styles={{ root: { height: 28 }, label: { fontSize: 12 } }}
                            />
                        </div>
                    </StyleGroupAccordion>
                ))}
            </div>
        );
    };

    const usageCount = deleteTarget ? countStyleRefs(deleteTarget.id, props.usageScanRoots ?? []) : 0;
    const hasScan = !!props.usageScanRoots?.length;

    // Deleting the ACTIVE palette / font pack is allowed - every token pointing at it simply stops
    // resolving and those properties drop back to their defaults - but that is a big enough change
    // to spell out rather than bury under the generic "elements keep their own settings" wording.
    const deleteSubText = (): string => {
        if (!deleteTarget) return '';
        if (isTokenElementType(deleteTarget.elementType)) {
            const noun = STYLE_ELEMENT_TYPE_SINGULAR[deleteTarget.elementType].toLowerCase();
            const activeWarning = deleteTarget.isActive === true
                ? ` This is the ACTIVE ${noun}: every value pointing at it stops resolving and falls back to its default until another ${noun} is made active.`
                : '';
            return `Delete "${deleteTarget.name}"?${activeWarning} This can't be undone.`;
        }
        if (!hasScan) return `Delete "${deleteTarget.name}"? Elements referencing it keep their own settings but lose the style's styling. This can't be undone.`;
        return usageCount > 0
            ? `"${deleteTarget.name}" is used by ${usageCount} element${usageCount === 1 ? '' : 's'} on this storefront. Those elements keep their own settings but lose the style's styling. This can't be undone.`
            : `"${deleteTarget.name}" isn't referenced by any element on this storefront. This can't be undone.`;
    };

    return (
        <>
            <Panel
                isOpen={props.isOpen}
                onDismiss={props.onDismiss}
                headerText={view.level === 'style' && draft ? draft.name : 'Storefront Styles'}
                type={PanelType.smallFixedFar}
                // Non-blocking so the page preview behind stays visible while a style is edited.
                isBlocking={false}
                isFooterAtBottom
                // The sticky footer is transparent by default, so scrolled rows would show
                // through behind Save/Cancel — give it an opaque surface and a divider.
                styles={{ footer: { backgroundColor: theme.palette.white, borderTop: `1px solid ${theme.palette.neutralLight}` } }}
                closeButtonAriaLabel='Close'
                onRenderFooterContent={view.level === 'style' ? () => (
                    <Stack horizontal tokens={{ childrenGap: 8 }}>
                        <PrimaryButton text='Save' iconProps={{ iconName: 'Save' }} disabled={busy} onClick={onSaveClicked} />
                        <DefaultButton text='Cancel' disabled={busy} onClick={closeStyle} />
                    </Stack>
                ) : undefined}
            >
                {props.isLoading && <Spinner size={SpinnerSize.medium} label='Loading styles' />}
                {!props.isLoading && view.level === 'root' && renderRoot()}
                {!props.isLoading && view.level === 'category' && renderCategory(view.elementType)}
                {!props.isLoading && view.level === 'style' && renderStyleEditor(view.elementType)}
            </Panel>

            {/* Advanced escape: the existing key/value CSS editor, bound to this part's dict. */}
            {draft && advancedPart && (
                <StorefrontSectionAdvancedStyleEditorModal
                    css={(draft.parts?.[advancedPart] ?? {}) as Css}
                    displayModal
                    closer={() => setAdvancedPart(undefined)}
                    onChange={(css) => replacePart(advancedPart, css)}
                />
            )}

            <Dialog
                hidden={!renameTarget}
                onDismiss={() => setRenameTarget(undefined)}
                dialogContentProps={{ type: DialogType.normal, title: 'Rename style' }}
            >
                <TextField label='Name' value={renameValue} onChange={(_, v) => setRenameValue(v ?? '')} autoFocus />
                <DialogFooter>
                    <PrimaryButton text='Rename' disabled={busy || !renameValue.trim()} onClick={() => { void commitRename(); }} />
                    <DefaultButton text='Cancel' onClick={() => setRenameTarget(undefined)} />
                </DialogFooter>
            </Dialog>

            <Dialog
                hidden={!deleteTarget}
                onDismiss={() => setDeleteTarget(undefined)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: deleteTarget && isTokenElementType(deleteTarget.elementType)
                        ? `Delete ${STYLE_ELEMENT_TYPE_SINGULAR[deleteTarget.elementType].toLowerCase()}`
                        : 'Delete style',
                    subText: deleteSubText(),
                }}
            >
                <DialogFooter>
                    <PrimaryButton text='Delete' disabled={busy} onClick={() => { void commitDelete(); }} />
                    <DefaultButton text='Cancel' onClick={() => setDeleteTarget(undefined)} />
                </DialogFooter>
            </Dialog>

            <Dialog
                hidden={!promoteTarget}
                onDismiss={() => setPromoteTarget(undefined)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Move to Global',
                    subText: `"${promoteTarget?.name}" becomes available to all storefronts in your organization. Elements already using it keep working, and future edits apply everywhere — global styles bypass preview.`,
                }}
            >
                <DialogFooter>
                    <PrimaryButton text='Move to Global' disabled={busy} onClick={() => { void commitPromote(); }} />
                    <DefaultButton text='Cancel' onClick={() => setPromoteTarget(undefined)} />
                </DialogFooter>
            </Dialog>

            <Dialog
                hidden={!applyAllTarget}
                onDismiss={() => setApplyAllTarget(undefined)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: applyAllTarget ? `Apply to all ${STYLE_ELEMENT_TYPE_LABELS[applyAllTarget.elementType].toLowerCase()}` : 'Apply to all',
                    subText: applyAllTarget
                        ? `"${applyAllTarget.name}" will be attached to every matching element below. Each element keeps any styling of its own — that always wins over the style. Nothing is saved until you exit Edit Mode.`
                        : undefined,
                }}
            >
                {applyAllTarget && (
                    <Stack tokens={{ childrenGap: 4 }} style={{ maxHeight: 240, overflowY: 'auto' }}>
                        {bulkApplyPlanFor(applyAllTarget.elementType).map((entry) => (
                            <div key={entry.configField} style={{ fontSize: 12 }}>
                                <strong>{entry.label}</strong>
                                <span style={{ color: theme.palette.neutralSecondary }}> — {entry.keys.join(', ')}</span>
                            </div>
                        ))}
                        <div style={{ fontSize: 11, color: theme.palette.neutralSecondary, marginTop: 4 }}>
                            Sections keep their own styling — use each section's Appearance tab to restyle those.
                        </div>
                    </Stack>
                )}
                <DialogFooter>
                    <PrimaryButton text='Apply' disabled={busy} onClick={commitApplyAll} />
                    <DefaultButton text='Cancel' onClick={() => setApplyAllTarget(undefined)} />
                </DialogFooter>
            </Dialog>

            {/* "Scan storefront colors" - only inside a palette's editor, and only when the host
                wired the working settings (the scan source and one of the write targets). */}
            {draft && draft.elementType === 'palette' && props.workingSettings && props.onWorkingSettingsChange && (
                <PaletteTokenizeDialog
                    hidden={!tokenizeOpen}
                    onDismiss={() => setTokenizeOpen(false)}
                    palette={draft}
                    settings={props.workingSettings}
                    styles={styles}
                    busy={busy}
                    onApply={(mapping) => { void commitTokenize(mapping); }}
                />
            )}

            <Dialog
                hidden={!confirmGlobalSave}
                onDismiss={() => setConfirmGlobalSave(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Save global style',
                    subText: `"${draft?.name}" is a Global Style — these changes apply to every storefront using it, immediately.`,
                }}
            >
                <DialogFooter>
                    <PrimaryButton text='Save everywhere' disabled={busy} onClick={() => { setConfirmGlobalSave(false); void saveDraft(); }} />
                    <DefaultButton text='Cancel' onClick={() => setConfirmGlobalSave(false)} />
                </DialogFooter>
            </Dialog>
        </>
    );
};

export default StorefrontStylesPanel;
